<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import { catalogFilterKey } from '@/domain/catalog/catalog-filter-copy';

  /**
   * What the list shows under a filter or a search (plan §5.7, §5.16):
   * «40 productos sin foto · Toque la cámara para agregar una.», «7
   * resultados para "pizza"», with «Quitar filtro» / «Quitar búsqueda».
   */
  const { state, data, url } = useCatalogPage();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const tc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));

  const query = computed(() => data.debouncedQuery.value.trim());
  const showDiscounts = computed(
    () => !!data.restaurant.value?.settings?.showDiscounts
  );
  const filtered = computed(() => state.filter !== 'all');

  /** Discounted products the storefront does not show (no photo). */
  const discountedWithoutPhoto = computed(() =>
    data.shownSections.value.reduce(
      (sum, section) =>
        sum + section.items.filter((item) => !item.picture).length,
      0
    )
  );

  const text = computed(() => {
    const n = data.shownCount.value;
    if (query.value)
      return tc('menuPage.result.search', n, { n, q: query.value });
    const key = catalogFilterKey(state.filter);
    const parts = [tc(`menuPage.result.filter.${key}`, n, { n })];
    if (state.filter === 'discounted') {
      // «Descuentos» exists on the storefront only while it is switched on.
      if (!showDiscounts.value) return parts.join(' · ');
      if (discountedWithoutPhoto.value)
        parts.push(
          tc(
            'menuPage.result.discountedNoPhoto',
            discountedWithoutPhoto.value,
            {
              m: discountedWithoutPhoto.value,
            }
          )
        );
      else if (n) parts.push(t('menuPage.result.hint.discounted'));
    } else if (n && state.filter !== 'scheduled')
      parts.push(t(`menuPage.result.hint.${key}`));
    return parts.join(' · ');
  });
</script>

<template>
  <p class="v2-catalog-result-line" role="status">
    <span class="result-text">{{ text }}</span>
    <button
      v-if="query"
      type="button"
      class="result-action"
      @click="url.clearSearch()"
    >
      {{ t('menuPage.result.clearSearch') }}
    </button>
    <button
      v-if="filtered"
      type="button"
      class="result-action"
      @click="url.setFilter('all')"
    >
      {{ t('menuPage.result.clearFilter') }}
    </button>
  </p>
</template>

<style scoped lang="scss">
  .v2-catalog-result-line {
    @apply tw-m-0 tw-flex tw-flex-wrap tw-items-center tw-pb-1 tw-text-sm tw-text-neutral-600;

    .result-text {
      @apply tw-mr-3 tw-py-1;
    }

    .result-action {
      @apply tw-relative tw-mr-3 tw-cursor-pointer tw-rounded tw-border-0 tw-bg-transparent tw-p-0 tw-py-1 tw-text-sm tw-font-semibold tw-text-neutral-900 tw-underline;

      // 44 px to the touch.
      &::after {
        @apply tw-absolute tw-left-0 tw-right-0;
        content: '';
        top: -10px;
        bottom: -10px;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
  }
</style>
